/**
 * Animated Orb Component
 *
 * A glowing orb that animates along the SVG tree connector path
 * when a trial completes in a sub-study.
 */

import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useMemo, useRef } from "react";
import { calculatePathLength, interpolateOrbPath } from "@/utils/bezierPath";
import type { GoldenTier } from "./OrbManager";

interface AnimatedOrbProps {
  /** Y position of the sub-study card center (relative to SVG) */
  cardCenterY: number;
  /** Callback when animation completes */
  onComplete: () => void;
  /** Unique key for this orb instance */
  orbKey: string;
  /** Optional color override (default: blue-400) */
  color?: string;
  /**
   * Golden orb tier - controls appearance based on achievement type
   * 'none' = regular blue orb
   * 'personal' = beat own best (gold color, normal size)
   * 'overall' = beat group's best (gold color, 1.6x size, slower)
   */
  goldenTier?: GoldenTier;
}

// Golden orb configuration for 'overall' tier (beat group's best)
// Celebratory variant with larger size and slower animation
const OVERALL_GOLDEN_CONFIG = {
  color: "#fbbf24", // amber-400 - warm golden yellow
  sizeMultiplier: 1.6, // 60% larger than regular orbs
  speedMultiplier: 0.5, // Half speed (twice the duration) for majestic effect
} as const;

// Golden orb configuration for 'personal' tier (beat own best)
// Same gold color but normal size and speed
const PERSONAL_GOLDEN_CONFIG = {
  color: "#fbbf24", // amber-400 - same gold color as overall
  sizeMultiplier: 1, // Normal size
  speedMultiplier: 1, // Normal speed
} as const;

export function AnimatedOrb({
  cardCenterY,
  onComplete,
  orbKey,
  color = "#60a5fa", // blue-400
  goldenTier = 'none',
}: AnimatedOrbProps) {
  // Animated progress value from 0 to 1
  const progress = useMotionValue(0);

  // Use ref for callback to prevent animation restart when callback identity changes
  // This is critical - without this, spawning a new orb would restart all existing orbs
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Determine orb appearance based on golden tier
  const orbConfig = useMemo(() => {
    switch (goldenTier) {
      case 'overall':
        return OVERALL_GOLDEN_CONFIG;
      case 'personal':
        return PERSONAL_GOLDEN_CONFIG;
      default:
        return { color, sizeMultiplier: 1, speedMultiplier: 1 };
    }
  }, [goldenTier, color]);

  const orbColor = orbConfig.color;
  const sizeScale = orbConfig.sizeMultiplier;

  // Calculate animation duration based on path length
  // Longer paths = proportionally longer duration for consistent visible speed
  // Overall golden orbs move slower for a more majestic, celebratory effect
  const duration = useMemo(() => {
    const pathLength = calculatePathLength(cardCenterY);
    // Target speed: ~250px per second for comfortable visibility
    const baseSpeed = 250; // px per second
    const targetSpeed = baseSpeed * orbConfig.speedMultiplier;
    const calculatedDuration = pathLength / targetSpeed;
    // Clamp between 0.8s (short paths) and 8s (very long overall golden paths)
    const maxDuration = goldenTier === 'overall' ? 8 : 5;
    return Math.max(0.8, Math.min(maxDuration, calculatedDuration));
  }, [cardCenterY, goldenTier, orbConfig.speedMultiplier]);

  // Derive x, y, and opacity from progress
  const position = useTransform(progress, (p) => {
    return interpolateOrbPath(p, { cardCenterY });
  });

  const x = useTransform(position, (pos) => pos.x);
  const y = useTransform(position, (pos) => pos.y);
  const opacity = useTransform(position, (pos) => pos.opacity);

  // Start animation on mount - only runs once per orb
  // Using onCompleteRef ensures callback identity changes don't restart animation
  useEffect(() => {
    // Reset to start
    progress.set(0);

    // Use framer-motion's animate function for smooth animation
    const controls = animate(progress, 1, {
      duration,
      ease: [0.25, 0.1, 0.25, 1], // cubic-bezier for smooth acceleration/deceleration
      onComplete: () => onCompleteRef.current(),
    });

    return () => {
      controls.stop();
    };
    // Note: onComplete intentionally excluded - we use onCompleteRef to prevent restarts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, duration]);

  return (
    <motion.g key={orbKey} style={{ opacity }}>
      {/* Outer glow (largest, most diffuse) */}
      <motion.circle
        cx={x}
        cy={y}
        r={12 * sizeScale}
        fill={orbColor}
        opacity={0.2}
        style={{
          filter: `blur(${6 * sizeScale}px)`,
        }}
      />

      {/* Inner glow */}
      <motion.circle
        cx={x}
        cy={y}
        r={8 * sizeScale}
        fill={orbColor}
        opacity={0.5}
        style={{
          filter: `blur(${3 * sizeScale}px)`,
        }}
      />

      {/* Core orb */}
      <motion.circle
        cx={x}
        cy={y}
        r={5 * sizeScale}
        fill={orbColor}
      />

      {/* Bright center highlight */}
      <motion.circle
        cx={x}
        cy={y}
        r={2 * sizeScale}
        fill="white"
        opacity={0.9}
      />
    </motion.g>
  );
}

export default AnimatedOrb;
